import React, { useEffect, useRef, useState } from "react";

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;
// One to three emoji and nothing else: shown large, like other chat apps.
const EMOJI_ONLY_RE = /^(?:\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|\p{Emoji_Modifier})*\s*){1,3}$/u;

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function Linkified({ text }) {
  const parts = String(text || "").split(URL_RE);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part} target="_blank" rel="noreferrer noopener">
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

const FILE_ICONS = [
  [/pdf/, "fa-file-pdf", "pdf"],
  [/word|msword|document/, "fa-file-word", "doc"],
  [/sheet|excel|csv/, "fa-file-excel", "xls"],
  [/presentation|powerpoint/, "fa-file-powerpoint", "ppt"],
  [/zip|compressed|rar/, "fa-file-zipper", "zip"],
  [/text/, "fa-file-lines", "txt"],
];

function fileIcon(type, name) {
  const t = `${type || ""} ${name || ""}`.toLowerCase();
  const hit = FILE_ICONS.find(([re]) => re.test(t));
  return hit ? { icon: hit[1], tone: hit[2] } : { icon: "fa-file", tone: "other" };
}

function extension(name) {
  const match = /\.([a-z0-9]{1,5})$/i.exec(name || "");
  return match ? match[1].toUpperCase() : "";
}

// Files of a message: photos as square tiles side by side (a single photo
// stays large), audio/video players, and other files as square tiles.
// Items come from the server ({url, name, content_type, size_bytes}) or, while
// sending, from the browser ({preview, name, content_type, size_bytes}).
function Attachments({ items, pending }) {
  const images = items.filter((a) => String(a.content_type || "").startsWith("image/"));
  const media = items.filter((a) => /^(audio|video)\//.test(String(a.content_type || "")));
  const files = items.filter((a) => !images.includes(a) && !media.includes(a));
  const href = (a) => (pending ? undefined : a.url);

  return (
    <div className="messages__files">
      {images.length === 1 ? (
        <a
          href={href(images[0])}
          target="_blank"
          rel="noreferrer"
          className="messages__image"
        >
          <img src={images[0].url || images[0].preview} alt={images[0].name} loading="lazy" />
        </a>
      ) : images.length > 1 ? (
        <div className={`messages__tiles messages__tiles--${Math.min(images.length, 3)}`}>
          {images.map((a, i) => (
            <a
              key={a.id || i}
              href={href(a)}
              target="_blank"
              rel="noreferrer"
              className="messages__tile messages__tile--image"
              title={a.name}
            >
              <img src={a.url || a.preview} alt={a.name} loading="lazy" />
            </a>
          ))}
        </div>
      ) : null}

      {media.map((a, i) =>
        String(a.content_type).startsWith("audio/") ? (
          <audio
            key={a.id || `m${i}`}
            className="messages__audio"
            controls
            preload="metadata"
            src={a.url || a.preview}
          />
        ) : (
          <video
            key={a.id || `m${i}`}
            className="messages__video"
            controls
            preload="metadata"
            src={a.url || a.preview}
          />
        ),
      )}

      {files.length ? (
        <div className={`messages__tiles messages__tiles--${Math.min(files.length, 3)}`}>
          {files.map((a, i) => {
            const { icon, tone } = fileIcon(a.content_type, a.name);
            return (
              <a
                key={a.id || `f${i}`}
                href={href(a)}
                target="_blank"
                rel="noreferrer"
                className={`messages__tile messages__tile--file is-${tone}`}
                title={`${a.name} (${formatSize(a.size_bytes)})`}
              >
                <i className={`fa-solid ${icon}`} aria-hidden="true" />
                <strong>{extension(a.name) || "FILE"}</strong>
                <span>{a.name}</span>
                <small dir="ltr">{formatSize(a.size_bytes)}</small>
              </a>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function MessageBubble({
  message,
  mine,
  showSender,
  grouped,
  canAct,
  reactions,
  myId,
  time,
  labels,
  onReact,
  onReply,
  onEdit,
  onDelete,
  onRetry,
  onJumpTo,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const deleted = message.deleted;
  const pending = message.pending;
  const failed = message.failed;
  const attachments = message.attachments || (message.attachment ? [message.attachment] : []);
  const localFiles = message.localFiles || [];
  const bigEmoji =
    !deleted && !attachments.length && !localFiles.length && EMOJI_ONLY_RE.test(String(message.body || "").trim());
  const actionable = canAct && !deleted && !pending && !failed;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.body || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // clipboard unavailable
    }
    setMenuOpen(false);
  };

  return (
    <div
      className={`messages__row${mine ? " is-mine" : ""}${grouped ? " is-grouped" : ""}`}
      id={`msg-${message.id}`}
    >
      <div className="messages__bubbleWrap">
        <div
          className={`messages__bubble${deleted ? " is-deleted" : ""}${bigEmoji ? " is-emoji" : ""}${
            pending ? " is-pending" : ""
          }${failed ? " is-failed" : ""}`}
        >
          {showSender ? <div className="messages__sender">{message.sender?.name}</div> : null}

          {message.reply_to ? (
            <button
              type="button"
              className="messages__quote"
              onClick={() => onJumpTo?.(message.reply_to.id)}
            >
              <strong>{message.reply_to.sender?.name}</strong>
              <span>
                {message.reply_to.deleted
                  ? labels.deleted
                  : message.reply_to.body || (message.reply_to.has_attachment ? labels.attachment : "")}
              </span>
            </button>
          ) : null}

          {deleted ? (
            <p className="messages__text messages__text--deleted">
              <i className="fa-solid fa-ban" aria-hidden="true" /> {labels.deleted}
            </p>
          ) : (
            <>
              {attachments.length ? <Attachments items={attachments} /> : null}
              {localFiles.length ? <Attachments items={localFiles} pending /> : null}
              {message.body ? (
                <p className="messages__text">
                  <Linkified text={message.body} />
                </p>
              ) : null}
            </>
          )}

          <span className="messages__meta">
            {message.edited ? <span className="messages__edited">{labels.edited}</span> : null}
            <time dateTime={message.created_at}>{time}</time>
            {mine && pending ? <i className="fa-regular fa-clock" aria-label={labels.sending} /> : null}
          </span>
        </div>

        {reactions.length ? (
          <div className="messages__reactions">
            {reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`messages__reaction${r.user_ids?.includes(myId) ? " is-mine" : ""}`}
                onClick={() => actionable && onReact(r.emoji)}
                disabled={!actionable}
              >
                <span>{r.emoji}</span>
                {r.count > 1 ? <small>{r.count}</small> : null}
              </button>
            ))}
          </div>
        ) : null}

        {failed ? (
          <button type="button" className="messages__retry" onClick={onRetry}>
            <i className="fa-solid fa-rotate-right" aria-hidden="true" /> {labels.failed}
          </button>
        ) : null}
      </div>

      {actionable ? (
        <div className={`messages__actions${menuOpen ? " is-open" : ""}`} ref={menuRef}>
          <div className="messages__reactBar">
            {(labels.reactionList || []).map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReact(emoji)}
                aria-label={`${labels.react} ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
          <button type="button" className="messages__actBtn" onClick={onReply} title={labels.reply} aria-label={labels.reply}>
            <i className="fa-solid fa-reply" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="messages__actBtn"
            onClick={() => setMenuOpen((v) => !v)}
            title={labels.more}
            aria-label={labels.more}
            aria-expanded={menuOpen}
          >
            <i className="fa-solid fa-ellipsis" aria-hidden="true" />
          </button>
          {menuOpen ? (
            <div className="messages__menu" role="menu">
              {message.body ? (
                <button type="button" role="menuitem" onClick={copy}>
                  <i className="fa-regular fa-copy" aria-hidden="true" /> {copied ? labels.copied : labels.copy}
                </button>
              ) : null}
              {mine && (message.body || !attachments.length) ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit();
                  }}
                >
                  <i className="fa-solid fa-pen" aria-hidden="true" /> {labels.edit}
                </button>
              ) : null}
              {mine ? (
                <button
                  type="button"
                  role="menuitem"
                  className="is-danger"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                >
                  <i className="fa-solid fa-trash" aria-hidden="true" /> {labels.delete}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
